// src/components/form-builder/FormField.jsx
import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Paper,
  Typography,
  TextField,
  Box,
  Button,
  IconButton,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Tooltip,
  Collapse,
  Rating,
  Slider,
  InputAdornment,
  Switch,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  DragIndicator as DragIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ContentCopy as CopyIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  TableChart as TableIcon,
  Close as CloseIcon,
  LocationOn,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { getFieldTypeById } from '../../constants/formFields';
import ReactSignatureCanvas from 'react-signature-canvas';
import CalculationField from './fields/CalculationField';
// import PdfUploadArea from '../PdfUploadArea'; // Geçici olarak devre dışı

const TableEditor = ({ field, onUpdateField }) => {
  const [columns, setColumns] = useState(field.columns || []);
  const [maxRows, setMaxRows] = useState(field.maxRows || 10);
  const [minRows, setMinRows] = useState(field.minRows || 1);

  useEffect(() => {
    setColumns(field.columns || []);
    setMaxRows(field.maxRows || 10);
    setMinRows(field.minRows || 1);
  }, [field]);

  const handleAddColumn = () => {
    const newColumn = {
      header: `Sütun ${columns.length + 1}`,
      type: 'text',
      width: '200px',
      options: []
    };
    const updatedColumns = [...columns, newColumn];
    setColumns(updatedColumns);
    
    onUpdateField(field.id, { 
      ...field, 
      columns: updatedColumns,
      maxRows,
      minRows,
      showRowCount: field.showRowCount
    });
  };

  const handleUpdateColumn = (index, updates) => {
    const updatedColumns = columns.map((col, i) => 
      i === index ? { ...col, ...updates } : col
    );
    setColumns(updatedColumns);
    
    onUpdateField(field.id, { 
      ...field, 
      columns: updatedColumns,
      maxRows,
      minRows,
      showRowCount: field.showRowCount
    });
  };

  const handleRemoveColumn = (index) => {
    const updatedColumns = columns.filter((_, i) => i !== index);
    setColumns(updatedColumns);
    
    onUpdateField(field.id, { 
      ...field, 
      columns: updatedColumns,
      maxRows,
      minRows,
      showRowCount: field.showRowCount
    });
  };

  const handleRowSettingsChange = (updates) => {
    const updatedField = {
      ...field,
      ...updates,
      columns: columns
    };
    
    if ('minRows' in updates) setMinRows(updates.minRows);
    if ('maxRows' in updates) setMaxRows(updates.maxRows);
    
    onUpdateField(field.id, updatedField);
  };

  const renderTablePreview = () => (
    <Box sx={{ width: '100%', overflowX: 'auto', mt: 2 }}>
      <Table size="small" sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
        <TableHead>
          <TableRow>
            {field.columns?.map((column, index) => (
              <TableCell 
                key={index}
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                  width: column.width || 'auto'
                }}
              >
                {column.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {field.columns?.map((column, index) => (
              <TableCell key={index}>
                {column.type === 'select' ? (
                  <Select
                    disabled
                    size="small"
                    fullWidth
                    placeholder={column.placeholder}
                  >
                    {column.options?.map((opt, i) => (
                      <MenuItem key={i} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                ) : (
                  <TextField
                    disabled
                    size="small"
                    type={column.type}
                    fullWidth
                    placeholder={
                      column.type === 'number' ? 'Sayı giriniz' :
                      column.type === 'date' ? 'Tarih seçiniz' :
                      'Metin giriniz'
                    }
                  />
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
      {field.showRowCount && (
        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
          Minimum: {field.minRows} satır, Maksimum: {field.maxRows} satır
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Tablo Yapılandırması
      </Typography>
      
      {/* Satır Sayısı Ayarları */}
      <Box sx={{ 
        mb: 3, 
        p: 2, 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper'
      }}>
        <Typography variant="subtitle2" gutterBottom>
          Satır Sayısı Ayarları
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Minimum Satır"
            type="number"
            value={minRows}
            onChange={(e) => {
              const value = Math.max(1, parseInt(e.target.value) || 1);
              handleRowSettingsChange({ 
                minRows: value,
                maxRows: Math.max(value, maxRows)
              });
            }}
            InputProps={{ inputProps: { min: 1 } }}
            sx={{ width: 120 }}
          />
          <TextField
            size="small"
            label="Maksimum Satır"
            type="number"
            value={maxRows}
            onChange={(e) => {
              const value = Math.max(minRows, parseInt(e.target.value) || minRows);
              handleRowSettingsChange({ 
                maxRows: value,
                minRows: minRows
              });
            }}
            InputProps={{ inputProps: { min: minRows } }}
            sx={{ width: 120 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={field.showRowCount || false}
                onChange={(e) => {
                  handleRowSettingsChange({ 
                    showRowCount: e.target.checked
                  });
                }}
              />
            }
            label="Satır Sayısını Göster"
          />
        </Box>
      </Box>

      {columns.map((column, index) => (
        <Box
          key={index}
          sx={{
            p: 3,
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              label="Sütun Başlığı"
              value={column.header}
              onChange={(e) => handleUpdateColumn(index, { header: e.target.value })}
              fullWidth
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Tip</InputLabel>
              <Select
                value={column.type}
                label="Tip"
                onChange={(e) => handleUpdateColumn(index, { type: e.target.value })}
              >
                <MenuItem value="text">Metin</MenuItem>
                <MenuItem value="number">Sayı</MenuItem>
                <MenuItem value="date">Tarih</MenuItem>
                <MenuItem value="select">Seçim</MenuItem>
              </Select>
            </FormControl>
           {/*  <TextField
              size="small"
              label="Genişlik"
              value={column.width}
              onChange={(e) => handleUpdateColumn(index, { width: e.target.value })}
              sx={{ width: 100 }}
            /> */}
            <IconButton 
              color="error" 
              onClick={() => handleRemoveColumn(index)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>

          {column.type === 'select' && (
            <Box sx={{ ml: 2 }}>
              <Typography variant="caption" gutterBottom>
                Seçenekler
              </Typography>
              {column.options?.map((option, optIndex) => (
                <Box key={optIndex} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    size="small"
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...column.options];
                      newOptions[optIndex] = e.target.value;
                      handleUpdateColumn(index, { options: newOptions });
                    }}
                    fullWidth
                    sx={{ mr: 1 }}
                  />
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => {
                      const newOptions = column.options.filter((_, i) => i !== optIndex);
                      handleUpdateColumn(index, { options: newOptions });
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  const newOptions = [...(column.options || []), `Seçenek ${(column.options?.length || 0) + 1}`];
                  handleUpdateColumn(index, { options: newOptions });
                }}
                sx={{ mt: 1 }}
              >
                Seçenek Ekle
              </Button>
            </Box>
          )}
        </Box>
      ))}
      
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddColumn}
        fullWidth
        sx={{ mt: 2 }}
      >
        Yeni Sütun Ekle
      </Button>
    </Box>
  );
};

const FormField = ({ 
  field, 
  onRemove, 
  onUpdate, 
  onDuplicate, 
  dragHandleProps,
  calculationValue,
  onCalculationChange 
}) => {
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);
  const [isEditing, setIsEditing] = useState(false);
  const [editedField, setEditedField] = useState(field);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const signatureRef = useRef(null);
  const [conditionalFields, setConditionalFields] = useState(field.conditionalFields || []);

  // PDF upload fonksiyonu
  const uploadPdfFunc = async (file) => {
    var bodyForm = new FormData();
    bodyForm.append("File", file);
    return axios.post("https://app.hhoms.com/api/panel/pdf", bodyForm, {
      headers: { ApplicationToken: appToken, User: userData.id },
    });
  };

  useEffect(() => {
    setEditedField(field);
    setConditionalFields(field.conditionalFields || []);
  }, [field]);

  const fieldType = getFieldTypeById(field.type);

  const handleSave = () => {
    if (field.type === 'table') {
      onUpdate(field.id, {
        ...field,
        label: editedField.label,
        required: editedField.required,
        size: editedField.size,
        columns: editedField.columns,
        minRows: editedField.minRows,
        maxRows: editedField.maxRows,
        showRowCount: editedField.showRowCount
      });
    } else {
      onUpdate(field.id, { ...editedField, signature: signatureData });
    }
    setIsEditing(false);
  };

  const handleTableUpdate = (fieldId, updates) => {
    setEditedField(prev => ({
      ...prev,
      ...updates
    }));
    onUpdate(fieldId, updates);
  };

  const handleAddOption = () => {
    if (!editedField.options) {
      setEditedField({ ...editedField, options: [''] });
    } else {
      setEditedField({ ...editedField, options: [...editedField.options, ''] });
    }
  };

  const handleRemoveOption = (index) => {
    const newOptions = editedField.options.filter((_, i) => i !== index);
    setEditedField({ ...editedField, options: newOptions });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...editedField.options];
    newOptions[index] = value;
    setEditedField({ ...editedField, options: newOptions });
  };

  const handleCalculationFieldChange = (value) => {
    if (onCalculationChange) {
      onCalculationChange(field.id, value);
    }
  };

  const renderFieldEditor = () => (
    <Box sx={{ mt: 2 }}>
      <TextField
        fullWidth
        label=""
        value={editedField.label || ''}
        onChange={(e) => setEditedField({ ...editedField, label: e.target.value })}
        sx={{ mb: 2 }}
      />
      
      {/* Tablo tipi değilse placeholder ve helpText göster */}
      {field.type !== 'table' && (
        <>
          <TextField
            fullWidth
            label="Açıklama"
            value={editedField.helpText || ''}
            onChange={(e) => setEditedField({ ...editedField, helpText: e.target.value })}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Placeholder"
            value={editedField.placeholder || ''}
            onChange={(e) => setEditedField({ ...editedField, placeholder: e.target.value })}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {/* Boyutlandırma Seçenekleri */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <Select
          value={editedField.size || '12'} // Varsayılan olarak 12
          onChange={(e) => setEditedField({ ...editedField, size: e.target.value })}
        >
          <MenuItem value="12">Tam Genişlik (12)</MenuItem>
          <MenuItem value="6">Yan Yana (6)</MenuItem>
          <MenuItem value="4">Üçlü (4)</MenuItem>
        </Select>
      </FormControl>

      {/* Alan tipine özel ayarlar */}
      {field.type === 'number' && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        </Box>
      )}

      {(field.type === 'file' || field.type === 'image' || field.type === 'pdf') && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Dosya Ayarları
          </Typography>
          
          <TextField
            fullWidth
            label="Maksimum Dosya Boyutu (MB)"
            type="number"
            value={Math.round((editedField.maxFileSize || 10485760) / 1048576)}
            onChange={(e) => setEditedField({ 
              ...editedField, 
              maxFileSize: parseInt(e.target.value) * 1048576 
            })}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Maksimum Dosya Sayısı"
            type="number"
            value={editedField.maxFiles || 1}
            onChange={(e) => setEditedField({ 
              ...editedField, 
              maxFiles: parseInt(e.target.value) 
            })}
            sx={{ mb: 2 }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={editedField.allowMultiple || false}
                onChange={(e) => setEditedField({ 
                  ...editedField, 
                  allowMultiple: e.target.checked 
                })}
              />
            }
            label="Çoklu dosya seçimine izin ver"
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Yükleme Metni"
            value={editedField.uploadText || ''}
            onChange={(e) => setEditedField({ 
              ...editedField, 
              uploadText: e.target.value 
            })}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Yükleme Alt Metni"
            value={editedField.uploadSubText || ''}
            onChange={(e) => setEditedField({ 
              ...editedField, 
              uploadSubText: e.target.value 
            })}
            sx={{ mb: 2 }}
          />
        </Box>
      )}

      {/* PDF Doküman Eki - Sadeleştirilmiş */}
      {field.type === 'pdfDocument' && (
        <Box sx={{ mb: 2 }}>
          {/* PDF Dosya Yükleme Alanı */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              PDF Dosyası
            </Typography>
            <input
              type="file"
              accept=".pdf"
              style={{ display: 'none' }}
              id={`pdf-upload-${field.id}`}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (file) {
                  try {
                    console.log('PDF yükleniyor:', file.name);
                    
                    // Base64'e çevir
                    const reader = new FileReader();
                    reader.onload = async (event) => {
                      try {
                        // Base64'ü blob'a çevir
                        const base64Data = event.target.result.split(',')[1];
                        const byteCharacters = atob(base64Data);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                          byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: file.type });
                        
                        // Server'a upload et
                        const uploadResponse = await uploadPdfFunc(blob);
                        console.log('PDF upload response:', uploadResponse.data);
                        
                        // Server'dan dönen URL'i al
                        let pdfUrl = null;
                        if (uploadResponse.data && uploadResponse.data.pdf) {
                          pdfUrl = uploadResponse.data.pdf;
                        }
                        
                        console.log('PDF URL:', pdfUrl);
                        
                        // Field'ı güncelle
                        const fileData = {
                          id: Date.now(),
                          name: file.name,
                          size: file.size,
                          type: file.type,
                          base64: event.target.result,
                          url: URL.createObjectURL(file),
                          uploadDate: new Date().toISOString(),
                          pdfUrl: pdfUrl // Server'dan gelen URL
                        };
                        
                        setEditedField(prev => ({
                          ...prev,
                          uploadedFiles: [fileData],
                          uploadedFile: fileData,
                          pdfUrl: pdfUrl
                        }));
                        
                        // Parent component'e güncellemeyi bildir
                        onUpdate(field.id, {
                          uploadedFiles: [fileData],
                          uploadedFile: fileData,
                          pdfUrl: pdfUrl
                        });
                        
                        console.log('PDF başarıyla yüklendi ve kaydedildi:', pdfUrl);
                        
                      } catch (uploadError) {
                        console.error('PDF upload error:', uploadError);
                        
                        // Upload başarısız olsa bile local olarak kaydet
                        const fileData = {
                          id: Date.now(),
                          name: file.name,
                          size: file.size,
                          type: file.type,
                          base64: event.target.result,
                          url: URL.createObjectURL(file),
                          uploadDate: new Date().toISOString(),
                          isLocal: true
                        };
                        
                        setEditedField(prev => ({
                          ...prev,
                          uploadedFiles: [fileData],
                          uploadedFile: fileData
                        }));
                        
                        onUpdate(field.id, {
                          uploadedFiles: [fileData],
                          uploadedFile: fileData
                        });
                        
                        console.log('PDF local olarak kaydedildi:', fileData);
                      }
                    };
                    reader.readAsDataURL(file);
                  } catch (error) {
                    console.error('PDF okuma hatası:', error);
                  }
                }
              }}
            />
            
            <Box
              component="label"
              htmlFor={`pdf-upload-${field.id}`}
              sx={{
                display: 'block',
                p: 2,
                border: '2px dashed #ccc',
                borderRadius: 1,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: '#fafafa',
                '&:hover': {
                  bgcolor: '#f0f0f0',
                  borderColor: '#999'
                }
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {(editedField.uploadedFiles && editedField.uploadedFiles.length > 0) ? 
                  `Seçilen: ${editedField.uploadedFiles[0].name}` : 
                  editedField.uploadedFile ? 
                    `Seçilen: ${editedField.uploadedFile.name}` : 
                    'PDF dosyası seçmek için tıklayın'
                }
              </Typography>
              {((editedField.uploadedFiles && editedField.uploadedFiles.length > 0) || editedField.uploadedFile) && (
                <Box>
                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                    ✓ PDF yüklendi
                  </Typography>
                  {editedField.pdfUrl && (
                    <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>
                      🔗 Server URL: {editedField.pdfUrl.substring(0, 50)}...
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            
            {/* PDF'i kaldırma butonu */}
            {((editedField.uploadedFiles && editedField.uploadedFiles.length > 0) || editedField.uploadedFile) && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => {
                  setEditedField(prev => ({
                    ...prev,
                    uploadedFiles: [],
                    uploadedFile: null,
                    pdfUrl: null
                  }));
                  
                  onUpdate(field.id, {
                    uploadedFiles: [],
                    uploadedFile: null,
                    pdfUrl: null
                  });
                }}
                sx={{ mt: 1 }}
              >
                PDF'i Kaldır
              </Button>
            )}
          </Box>
        </Box>
      )}

      {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Seçenekler
          </Typography>
          {editedField.options?.map((option, index) => (
            <Box key={index} sx={{ display: 'flex', mb: 1, gap: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                value={option}
                onChange={(e) => handleOptionChange(index, e.target.value)}
              />
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemoveOption(index)}
              >
                <RemoveIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddOption}
            size="small"
            sx={{ mt: 1, mb: 2 }}
          >
            Seçenek Ekle
          </Button>

          {/* Koşullu Alan Ayarları */}
          <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Koşullu Alan Ayarları
            </Typography>
            {conditionalFields.map((condition, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Seçenek</InputLabel>
                    <Select
                      value={condition.option}
                      label="Seçenek"
                      onChange={(e) => {
                        const updatedConditions = [...conditionalFields];
                        updatedConditions[index] = { ...condition, option: e.target.value };
                        setConditionalFields(updatedConditions);
                        setEditedField({ ...editedField, conditionalFields: updatedConditions });
                      }}
                    >
                      {editedField.options?.map((opt, i) => (
                        <MenuItem key={i} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      const updatedConditions = conditionalFields.filter((_, i) => i !== index);
                      setConditionalFields(updatedConditions);
                      setEditedField({ ...editedField, conditionalFields: updatedConditions });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Başlık"
                  value={condition.title || ''}
                  onChange={(e) => {
                    const updatedConditions = [...conditionalFields];
                    updatedConditions[index] = { ...condition, title: e.target.value };
                    setConditionalFields(updatedConditions);
                    setEditedField({ ...editedField, conditionalFields: updatedConditions });
                  }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Placeholder Metni"
                  value={condition.placeholder || ''}
                  onChange={(e) => {
                    const updatedConditions = [...conditionalFields];
                    updatedConditions[index] = { ...condition, placeholder: e.target.value };
                    setConditionalFields(updatedConditions);
                    setEditedField({ ...editedField, conditionalFields: updatedConditions });
                  }}
                />
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                const newCondition = { 
                  option: '', 
                  title: '',
                  placeholder: '',
                  content: '' 
                };
                const updatedConditions = [...conditionalFields, newCondition];
                setConditionalFields(updatedConditions);
                setEditedField({ ...editedField, conditionalFields: updatedConditions });
              }}
              size="small"
              sx={{ mt: 1 }}
            >
              Koşul Ekle
            </Button>
          </Box>
        </Box>
      )}

      {field.type === 'table' && (
        <TableEditor 
          field={editedField}
          onUpdateField={handleTableUpdate}
        />
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={editedField.required || false}
            onChange={(e) => setEditedField({ ...editedField, required: e.target.checked })}
          />
        }
        label="Zorunlu Alan"
        sx={{ mb: 2 }}
      />
    </Box>
  );

  const renderFieldPreview = () => {
    const sizeClass = editedField.size || '12';
    const widthPercentage = (sizeClass / 12) * 100;

    return (
      <Box sx={{ width: `${widthPercentage}%`, display: 'flex', flexDirection: 'column', mb: 2 }}>
        {(() => {
          switch (field.type) {
            case 'text':
            case 'email':
            case 'phone':
            case 'url':
            case 'file':
            case 'image':
              return (
                <TextField
                  fullWidth
                  type={field.type}
                  placeholder={field.type === 'number' ? 'Sayı giriniz' : field.placeholder}
                  disabled
                />
              );

            case 'pdf':
              return (
                <Box
                  sx={{
                    border: '2px solid #d32f2f',
                    borderRadius: 2,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: '#ffebee'
                  }}
                >
                  <PdfIcon sx={{ fontSize: 32, color: '#d32f2f' }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                      PDF Yükleme Alanı
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Kullanıcılar PDF dosyası yükleyebilir
                    </Typography>
                  </Box>
                </Box>
              );

            case 'pdfDocument':
              return (
                <Box
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    bgcolor: '#f9f9f9',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#f0f8f0',
                      borderColor: '#4caf50'
                    }
                  }}
                >
                  <PdfIcon sx={{ fontSize: 20, color: '#4caf50' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e7d32', fontSize: '13px' }}>
                      PDF Eki
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
                      {(editedField.uploadedFiles && editedField.uploadedFiles.length > 0) ? 
                        editedField.uploadedFiles[0].name : 
                        editedField.uploadedFile ? 
                          editedField.uploadedFile.name : 
                          'PDF eklenmedi'
                      }
                    </Typography>
                  </Box>
                  {((editedField.uploadedFiles && editedField.uploadedFiles.length > 0) || editedField.uploadedFile) && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: '#4caf50'
                      }}
                    />
                  )}
                </Box>
              );

            case 'number':
              return (
                <TextField
                  fullWidth
                  type="number"
                  placeholder="Sayı giriniz"
                  disabled
                />
              );

            case 'textarea':
              return (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder={field.placeholder}
                  disabled
                />
              );

            case 'select':
              return (
                <Box>
                  <FormControl fullWidth>
                    <InputLabel>{field.placeholder}</InputLabel>
                    <Select 
                      value="" 
                      disabled
                      onChange={(e) => {
                        const selectedOption = e.target.value;
                        const condition = field.conditionalFields?.find(c => c.option === selectedOption);
                        if (condition) {
                          // Koşullu alanı göster
                          console.log('Koşullu alan içeriği:', condition.content);
                        }
                      }}
                    >
                      {field.options?.map((option, index) => (
                        <MenuItem key={index} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {field.conditionalFields?.map((condition, index) => (
                    <Box key={index} sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {condition.option} seçildiğinde:
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={condition.content}
                        disabled
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  ))}
                </Box>
              );

            case 'checkbox':
              return field.options ? (
                <Box>
                  {field.options.map((option, index) => (
                    <Box key={index}>
                      <FormControlLabel
                        control={<Checkbox disabled />}
                        label={option}
                      />
                      {field.conditionalFields?.find(c => c.option === option) && (
                        <Box sx={{ ml: 4, mt: 1 }}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={field.conditionalFields.find(c => c.option === option).content}
                            disabled
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <FormControlLabel
                  control={<Checkbox disabled />}
                  label={field.label}
                />
              );

            case 'radio':
              return (
                <Box>
                  <RadioGroup>
                    {field.options?.map((option, index) => (
                      <Box key={index}>
                        <FormControlLabel
                          value={option}
                          control={<Radio disabled />}
                          label={option}
                        />
                        {field.conditionalFields?.find(c => c.option === option) && (
                          <Box sx={{ ml: 4, mt: 1 }}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              value={field.conditionalFields.find(c => c.option === option).content}
                              disabled
                            />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </RadioGroup>
                </Box>
              );

            case 'date':
            case 'time':
              return (
                <TextField
                  fullWidth
                  type={field.type}
                  disabled
                />
              );

            case 'file':
              return (
                <Box
                  sx={{
                    border: '2px solid #1976d2',
                    borderRadius: 3,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#e3f2fd',
                    color: '#1976d2',
                    minHeight: 120,
                    mb: 1,
                    boxShadow: 2
                  }}
                >
                  <FileIcon sx={{ fontSize: 48, mb: 1, color: '#1976d2' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Dosya Yükleme Alanı
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1976d2', mb: 1 }}>
                    Kullanıcılar buradan dosya yükleyebilir. Sürükleyip bırakabilir veya tıklayarak seçebilir.
                  </Typography>
                  <Typography variant="caption" color="#1976d2">
                    Desteklenen formatlar: PDF, Word, Excel, PowerPoint, TXT, CSV, ZIP, RAR, 7Z. Maksimum dosya sayısı: 5.
                  </Typography>
                </Box>
              );

            case 'image':
              return (
                <Box
                  sx={{
                    border: '2px solid #ffb300',
                    borderRadius: 3,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fff8e1',
                    color: '#ffb300',
                    minHeight: 120,
                    mb: 1,
                    boxShadow: 2
                  }}
                >
                  <ImageIcon sx={{ fontSize: 48, mb: 1, color: '#ffb300' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Görsel Yükleme Alanı
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ffb300', mb: 1 }}>
                    Kullanıcılar buradan görsel yükleyebilir. Sürükleyip bırakabilir veya tıklayarak seçebilir.
                  </Typography>
                  <Typography variant="caption" color="#ffb300">
                    Desteklenen formatlar: JPG, PNG, GIF, WEBP, BMP, TIFF. Maksimum görsel sayısı: 5.
                  </Typography>
                </Box>
              );

            case 'pdf':
              return (
                <Box
                  sx={{
                    border: '2px solid #d32f2f',
                    borderRadius: 3,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#ffebee',
                    color: '#d32f2f',
                    minHeight: 120,
                    mb: 1,
                    boxShadow: 2
                  }}
                >
                  <PdfIcon sx={{ fontSize: 48, mb: 1, color: '#d32f2f' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    PDF Yükleme Alanı
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#d32f2f', mb: 1 }}>
                    Kullanıcılar buradan PDF dosyası yükleyebilir. Sürükleyip bırakabilir veya tıklayarak seçebilir.
                  </Typography>
                  <Typography variant="caption" color="#d32f2f">
                    Desteklenen format: PDF. Maksimum dosya boyutu: 20MB.
                  </Typography>
                </Box>
              );

            case 'signature':
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Button
                    variant="outlined"
                    component="span"
                    sx={{ flexGrow: 1 }}
                    disabled
                  >
                    İmza Alanı
                  </Button>
                </Box>
              );

            case 'name':
              return (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Ad"
                    disabled
                  />
                  <TextField
                    fullWidth
                    label="Soyad"
                    disabled
                  />
                </Box>
              );

            case 'rating':
              return (
                <Rating
                  disabled
                  max={field.max || 5}
                />
              );

            case 'slider':
              return (
                <Slider
                  disabled
                  min={field.min || 0}
                  max={field.max || 100}
                  marks={field.marks}
                />
              );

            case 'currency':
              return (
                <TextField
                  fullWidth
                  disabled
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₺</InputAdornment>,
                  }}
                />
              );

            case 'table':
              return (
                <Box sx={{ width: '100%', overflowX: 'auto', mt: 2 }}>
                  <Table size="small" sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>
                    <TableHead>
                      <TableRow>
                        {field.columns?.map((column, index) => (
                          <TableCell 
                            key={index}
                            sx={{ 
                              fontWeight: 'bold',
                              backgroundColor: '#f5f5f5',
                              width: column.width || 'auto'
                            }}
                          >
                            {column.header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        {field.columns?.map((column, index) => (
                          <TableCell key={index}>
                            {column.type === 'select' ? (
                              <Select
                                disabled
                                size="small"
                                fullWidth
                                placeholder={column.placeholder}
                              >
                                {column.options?.map((opt, i) => (
                                  <MenuItem key={i} value={opt}>{opt}</MenuItem>
                                ))}
                              </Select>
                            ) : (
                              <TextField
                                disabled
                                size="small"
                                type={column.type}
                                fullWidth
                                placeholder={
                                  column.type === 'number' ? 'Sayı giriniz' :
                                  column.type === 'date' ? 'Tarih seçiniz' :
                                  'Metin giriniz'
                                }
                              />
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                  {field.showRowCount && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                      Minimum: {field.minRows} satır, Maksimum: {field.maxRows} satır
                    </Typography>
                  )}
                </Box>
              );

            case 'calculation':
              return (
                <CalculationField
                  field={field}
                  value={calculationValue}
                  onChange={handleCalculationFieldChange}
                  formData={{}}
                />
              );

            case 'address':
              return (
                <Box
                  sx={{
                    border: '2px solid #0097a7',
                    borderRadius: 3,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    bgcolor: '#e0f7fa',
                    color: '#0097a7',
                    minHeight: 120,
                    mb: 1,
                    boxShadow: 2
                  }}
                >
                  <LocationOn sx={{ fontSize: 40, mb: 1, color: '#0097a7' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    Adres Bilgisi
                  </Typography>
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField fullWidth size="small" label="İl" disabled />
                    <TextField fullWidth size="small" label="İlçe" disabled />
                    <TextField fullWidth size="small" label="Mahalle" disabled />
                    <TextField fullWidth size="small" label="Sokak" disabled />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField fullWidth size="small" label="Bina No" disabled />
                      <TextField fullWidth size="small" label="Daire No" disabled />
                    </Box>
                  </Box>
                </Box>
              );

            default:
              return null;
          }
        })()}
      </Box>
    );
  };

  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        position: 'relative',
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box {...dragHandleProps}>
          <Tooltip title="Sürükle">
            <DragIcon
              sx={{
                cursor: 'grab',
                color: 'action.active',
                mr: 1,
              }}
            />
          </Tooltip>
        </Box>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {field.label || fieldType?.label}
          {field.required && <span style={{ color: 'red' }}> *</span>}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {isEditing ? (
            <Tooltip title="Kaydet">
              <IconButton
                size="small"
                color="primary"
                onClick={handleSave}
              >
                <SaveIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <>
              <Tooltip title="Düzenle">
                <IconButton
                  size="small"
                  onClick={() => setIsEditing(true)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Çoğalt">
                <IconButton
                  size="small"
                  onClick={() => onDuplicate(field)}
                >
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title="Sil">
            <IconButton
              size="small"
              color="error"
              onClick={() => onRemove(field.id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {field.helpText && !isEditing && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1 }}
        >
          {field.helpText}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {isEditing ? renderFieldEditor() : renderFieldPreview()}
      </Box>
    </Paper>
  );
};

export default FormField;